import type { CSSProperties, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

/** Label 13px/500 #3f3f44 over a control, 7px gap (the prototype's form rows). */
export function Field({
  label,
  children,
  span,
  hint,
  required,
  style,
}: {
  label: string;
  children: ReactNode;
  /** grid-column: 1 / -1 */
  span?: boolean;
  /** A quiet asterisk after the label; screen readers hear "required". */
  required?: boolean;
  hint?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, gridColumn: span ? '1 / -1' : undefined, ...style }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: '#3f3f44' }}>
        {label}
        {required ? (
          <>
            <span aria-hidden title="Required" style={{ marginLeft: 3, color: '#8a8a8f', fontWeight: 400 }}>*</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : null}
      </label>
      {children}
      {hint ? <span style={{ fontSize: 12.5, color: '#8a8a8f' }}>{hint}</span> : null}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** height: 36 (account forms), 38 (uploader), 40 (auth) */
  h?: 36 | 38 | 40;
  mono?: boolean;
}

/** Input: 1px #e3e3e1, radius 7 (8 at 40px), padding 0 12, 14px (13.5 at 36px). */
export function Input({ h = 38, mono, style, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      style={{
        height: h,
        padding: '0 12px',
        border: '1px solid #e3e3e1',
        borderRadius: h === 40 ? 8 : 7,
        background: '#fff',
        fontSize: h === 36 ? 13.5 : 14,
        fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
        color: '#0f0f10',
        width: '100%',
        boxSizing: 'border-box',
        outline: 'none',
        ...style,
      }}
    />
  );
}

/** Textarea, all descriptions: min-height 72, padding 10 12, 14px, line-height 1.5, resize vertical. */
export function Textarea({ style, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      style={{
        minHeight: 72,
        padding: '10px 12px',
        border: '1px solid #e3e3e1',
        borderRadius: 7,
        fontSize: 14,
        lineHeight: 1.5,
        resize: 'vertical',
        fontFamily: 'inherit',
        color: '#0f0f10',
        background: '#fff',
        width: '100%',
        boxSizing: 'border-box',
        ...style,
      }}
    />
  );
}

/**
 * An input with something attached on its right, inside one 38px bordered
 * frame: the Study ID's wand button, the Age field's "90+" tag.
 */
export function AttachedInput({
  children,
  attachment,
  style,
}: {
  children: ReactNode;
  attachment?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 38,
        border: '1px solid #e3e3e1',
        borderRadius: 7,
        background: '#fff',
        overflow: 'hidden',
        ...style,
      }}
    >
      {children}
      {attachment}
    </div>
  );
}

/** The bare input inside AttachedInput (no border of its own). */
export function BareInput({ mono, style, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      style={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        padding: '0 12px',
        border: 0,
        outline: 0,
        fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
        fontSize: mono ? 13.5 : 14,
        color: '#0f0f10',
        background: 'transparent',
        ...style,
      }}
    />
  );
}

/** The attached tag: bg #fafaf9, left border #ebebea, 12px #6b6b70 ("90+"). */
export function AttachedTag({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        padding: '0 10px',
        fontSize: 12,
        color: '#6b6b70',
        borderLeft: '1px solid #ebebea',
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: '#fafaf9',
      }}
    >
      {children}
    </span>
  );
}

/** The attached 34px icon button (wand). */
export function AttachedButton({ title, onClick, children }: { title: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      className="tr-hover-track-ink"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        width: 34,
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        border: 0,
        borderLeft: '1px solid #ebebea',
        background: '#fafaf9',
        color: '#6b6b70',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

/**
 * The bordered search field used in the top bar (32px, #fafaf9) and in the
 * Browse Bar / uploader (30–36px, white). Icon on the left, optional clear
 * button on the right.
 */
export function SearchField({
  value,
  onChange,
  placeholder,
  h = 30,
  width,
  bg = '#fff',
  fontSize = 12.5,
  radius = 6,
  onClear,
  onFocus,
  onKeyDown,
  autoFocus,
  style,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  h?: number;
  width?: number | string;
  bg?: string;
  fontSize?: number;
  radius?: number;
  onClear?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  style?: CSSProperties;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width,
        height: h,
        boxSizing: 'border-box',
        padding: '0 10px',
        border: '1px solid #e3e3e1',
        borderRadius: radius,
        background: bg,
        color: '#8a8a8f',
        fontSize,
        ...style,
      }}
    >
      <svg width={h >= 32 ? 14 : 13} height={h >= 32 ? 14 : 13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={{ flex: 1, border: 0, background: 'transparent', fontSize, color: '#0f0f10', outline: 'none', minWidth: 0, fontFamily: 'inherit' }}
      />
      {onClear && value ? (
        <div
          title="Clear"
          className="tr-hover-line"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          style={{ width: 18, height: 18, borderRadius: '50%', display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#8a8a8f' }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </div>
      ) : null}
    </div>
  );
}
